import React, { useState } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect } from "react";
import CreateEventModal from "../Events/CreateEventModal";
import { getOverlayLocation } from "./helpers";

export default function MapSelection({
  setMapBounds,
  setOverlay,
  drawingModes,
  onSelect,
  setShowSidebar,
  selected,
  showCreateEventButton = true,
}: {
  setMapBounds?: any;
  setOverlay?: any;
  drawingModes?: ("marker" | "polygon" | "rectangle" | "circle")[];
  onSelect?: (area: any) => void;
  setShowSidebar?: (params: { show: boolean; bounds: any }) => void;
  selected?: any;
  showCreateEventButton?: boolean;
}) {
  const map = useMap();
  const drawing = useMapsLibrary("drawing");

  useEffect(() => {
    if (!map || !drawing) return;

    let shapeDrawn: any;

    // https://developers.google.com/maps/documentation/javascript/reference/drawing
    const newDrawingManager = new drawing.DrawingManager({
      map,
      drawingMode: null,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: drawingModes
          ? [
              ...(drawingModes.includes("marker")
                ? [google.maps.drawing.OverlayType.MARKER]
                : []),
              ...(drawingModes.includes("polygon")
                ? [google.maps.drawing.OverlayType.POLYGON]
                : []),
              ...(drawingModes.includes("rectangle")
                ? [google.maps.drawing.OverlayType.RECTANGLE]
                : []),
              ...(drawingModes.includes("circle")
                ? [google.maps.drawing.OverlayType.CIRCLE]
                : []),
            ]
          : [
              google.maps.drawing.OverlayType.MARKER,
              google.maps.drawing.OverlayType.POLYGON,
              google.maps.drawing.OverlayType.RECTANGLE,
              google.maps.drawing.OverlayType.CIRCLE,
            ],
      },
      markerOptions: {
        // draggable: true,
      },

      polygonOptions: {
        fillColor: "#00f",
        fillOpacity: 0.2,
        strokeWeight: 2,
        strokeColor: "#00f",
        strokeOpacity: 0.8,
      },
      rectangleOptions: {
        fillColor: "#00f",
        fillOpacity: 0.2,
        strokeWeight: 2,
        strokeColor: "#00f",
        strokeOpacity: 0.8,
      },
      circleOptions: {
        fillColor: "#00f",
        fillOpacity: 0.2,
        strokeWeight: 2,
        strokeColor: "#00f",
        strokeOpacity: 0.8,
      },
    });

    newDrawingManager.addListener("overlaycomplete", (event: any) => {
      // Source - https://stackoverflow.com/questions/3410600/convert-lat-lon-to-pixels-and-back
      // Posted by user3638471
      // Retrieved 2025-11-06, License - CC BY-SA 3.0
      let latLng;
      if (event.type === google.maps.drawing.OverlayType.MARKER) {
        latLng = event.overlay.getPosition();
      } else if (event.type === google.maps.drawing.OverlayType.POLYGON) {
        latLng = event.overlay.getPath().getAt(0);
      } else if (event.type === google.maps.drawing.OverlayType.RECTANGLE) {
        latLng = event.overlay.getBounds().getCenter();
      } else if (event.type === google.maps.drawing.OverlayType.CIRCLE) {
        latLng = event.overlay.getCenter();
      } else {
        return;
      }
      var projection = map.getProjection();
      var bounds = map.getBounds();
      if (!projection || !bounds) return;
      var topRight = projection?.fromLatLngToPoint(bounds.getNorthEast());
      var bottomLeft = projection?.fromLatLngToPoint(bounds.getSouthWest());
      var scale = Math.pow(2, map.getZoom() || 1);
      var worldPoint = projection?.fromLatLngToPoint(latLng);

      let xy = {
        x:
          Math.floor(((worldPoint?.x || 0) - (bottomLeft?.x || 0)) * scale) +
          map.getDiv().getBoundingClientRect().left,
        y:
          Math.floor(((worldPoint?.y || 0) - (topRight?.y || 0)) * scale) +
          map.getDiv().getBoundingClientRect().top,
      };

      onSelect?.(event);
      newDrawingManager.setDrawingMode(null);
      const showOverlay = (e: any) => {
        const location = event?.overlay
          ? getOverlayLocation(event?.overlay)
          : null;

        setOverlay({
          show: true,
          position: { x: e.domEvent.x, y: e.domEvent.y },
          content: (
            <div className="p-1 bg-purple-gradient rounded-1">
              {location && showCreateEventButton && (
                <CreateEventModal
                  showButton={
                    <button className="btn btn-outline-secondary border-0 w-100 text-start">
                      create event
                    </button>
                  }
                  initialData={{
                    Areas: [
                      {
                        type: "Feature",
                        geometry: location.Area,
                      },
                    ],
                    ContactsAndAssetsCriteria: {
                      IncludeAssets: [],
                      ExcludeAssets: [],
                      Coordinates: location.Area?.coordinates,
                      UseAssets: true,
                    },
                  }}
                />
              )}
              <button
                className="btn btn-outline-secondary border-0 w-100 text-start"
                onClick={() => {
                  onSelect?.(null);
                  event.overlay.setMap(null);
                  setOverlay((p: any) => ({ ...p, show: false }));
                }}
              >
                delete selection
              </button>
            </div>
          ),
        });
      };
      event.overlay.addListener("click", showOverlay);
      map.addListener("click", () => {
        setMapBounds(undefined);
        setOverlay((p: any) => ({ ...p, show: false }));
      });
      newDrawingManager.addListener("drawingmode_changed", () => {
        if (newDrawingManager.getDrawingMode()) {
          onSelect?.(null);
          event.overlay.setMap(null);
          setOverlay((p: any) => ({ ...p, show: false }));
        }
        setMapBounds(undefined);
      });
      shapeDrawn = event.overlay;
    });

    const paths = selected?.coordinates?.[0]?.[0]?.map(([lng, lat]: any) => ({
      lat,
      lng,
    }));

    let polygon: google.maps.Polygon | null = null;
    if (paths && paths.length > 0) {
      polygon = new google.maps.Polygon({
        paths,
        fillColor: "#00f",
        fillOpacity: 0.2,
        strokeWeight: 2,
        strokeColor: "#00f",
        strokeOpacity: 0.8,
      });
      polygon.setMap(map);
      const showOverlay = (e: any) => {
        setOverlay({
          show: true,
          position: { x: e.domEvent.x, y: e.domEvent.y },
          content: (
            <div className="p-1 bg-purple-gradient rounded-1">
              <button
                className="btn btn-outline-secondary border-0 w-100"
                onClick={() => {
                  onSelect?.(null);
                  polygon!.setMap(null);
                  setOverlay((p: any) => ({ ...p, show: false }));
                }}
              >
                delete selection
              </button>
            </div>
          ),
        });
      };
      polygon.addListener("click", showOverlay);
    }

    return () => {
      newDrawingManager.setMap(null);
      polygon?.setMap(null);
      if (shapeDrawn) {
        shapeDrawn.setMap(null);
      }
    };
  }, [drawing, map]);

  return <></>;
}
